import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const CommerceSectionExpandContext = createContext(null);

export function useCommerceSectionExpandRegistry() {
  const extrasRef = useRef({});
  const [totalExtraPx, setTotalExtraPx] = useState(0);

  const reportCommerceExpand = useCallback((compId, extraPx) => {
    const id = String(compId || '').trim();
    if (!id) return;
    extrasRef.current[id] = Math.max(0, Math.floor(Number(extraPx) || 0));
    const next = Math.max(0, ...Object.values(extrasRef.current), 0);
    setTotalExtraPx(next);
  }, []);

  const providerValue = useMemo(
    () => ({ reportCommerceExpand }),
    [reportCommerceExpand],
  );

  const CommerceSectionExpandProvider = useMemo(
    () =>
      function CommerceSectionExpandProviderInner({ children }) {
        return (
          <CommerceSectionExpandContext.Provider value={providerValue}>
            {children}
          </CommerceSectionExpandContext.Provider>
        );
      },
    [providerValue],
  );

  return { totalExtraPx, CommerceSectionExpandProvider };
}

export function useCommerceInlineSectionExpand({
  compId,
  enabled,
  slotHeightPx,
  rootRef,
}) {
  const ctx = useContext(CommerceSectionExpandContext);
  const report = ctx?.reportCommerceExpand;

  useEffect(() => {
    if (!enabled || !report || !compId) return undefined;
    const el = rootRef?.current;
    if (!el) return undefined;

    const budget = Math.max(1, Math.floor(Number(slotHeightPx) || 0));

    const measure = () => {
      const contentH = el.scrollHeight;
      const extra = Math.max(0, contentH - budget);
      report(compId, extra);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      ro.disconnect();
      report(compId, 0);
    };
  }, [compId, enabled, slotHeightPx, report, rootRef]);
}
